export interface IceConfiguration {
  servers: RTCIceServer[];
  turnConfigured: boolean;
}

export const readIceConfiguration = (): IceConfiguration => {
  if (remoteConfiguration) return remoteConfiguration;
  return readEnvironmentConfiguration();
};

let remoteConfiguration: IceConfiguration | null = null;
let remoteConfigurationRequest: Promise<IceConfiguration> | null = null;
let remoteConfigurationExpiresAt = 0;
const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * 1_000;

export const prepareIceConfiguration = async (sessionToken: string | null): Promise<IceConfiguration> => {
  const environmentConfiguration = readEnvironmentConfiguration();
  if (environmentConfiguration.turnConfigured || import.meta.env.DEV) return environmentConfiguration;
  if (remoteConfiguration && remoteConfigurationExpiresAt - Date.now() > REFRESH_BEFORE_EXPIRY_MS) return remoteConfiguration;
  remoteConfigurationRequest ??= fetchRemoteConfiguration(sessionToken)
    .catch(() => environmentConfiguration)
    .finally(() => { remoteConfigurationRequest = null; });
  return remoteConfigurationRequest;
};

const fetchRemoteConfiguration = async (sessionToken: string | null): Promise<IceConfiguration> => {
  if (!sessionToken) throw new Error("A signaling session is required for TURN.");
  const response = await fetch("/api/ice", {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${sessionToken}`,
    },
  });
  if (!response.ok) throw new Error("TURN credentials are unavailable.");
  const payload = await response.json() as unknown;
  const servers = parseIceServers(payload);
  const expiresAt = parseExpiry(payload);
  const configuration = {
    servers,
    turnConfigured: servers.some((server) => serverUrls(server).some((url) => url.startsWith("turn:"))),
  };
  if (!configuration.turnConfigured) throw new Error("TURN credentials are missing from the ICE response.");
  remoteConfiguration = configuration;
  remoteConfigurationExpiresAt = expiresAt;
  return configuration;
};

const readEnvironmentConfiguration = (): IceConfiguration => {
  const stunUrls = splitUrls(import.meta.env.VITE_STUN_URLS || "stun:stun.cloudflare.com:3478");
  const turnUrls = splitUrls(import.meta.env.VITE_TURN_URLS || import.meta.env.VITE_TURN_URL || "");
  const servers: RTCIceServer[] = [];
  if (stunUrls.length > 0) servers.push({ urls: stunUrls });
  if (turnUrls.length > 0) {
    servers.push({
      urls: turnUrls,
      username: import.meta.env.VITE_TURN_USERNAME || "",
      credential: import.meta.env.VITE_TURN_CREDENTIAL || "",
    });
  }
  return { servers, turnConfigured: turnUrls.length > 0 };
};

const parseIceServers = (value: unknown): RTCIceServer[] => {
  if (!isRecord(value) || !Array.isArray(value.iceServers)) throw new Error("Invalid ICE response.");
  return value.iceServers.map(parseIceServer);
};

const parseExpiry = (value: unknown): number => {
  if (!isRecord(value) || typeof value.expiresAt !== "number" || value.expiresAt <= Date.now()) {
    throw new Error("ICE credential expiry is invalid.");
  }
  return value.expiresAt;
};

const parseIceServer = (value: unknown): RTCIceServer => {
  if (!isRecord(value)) throw new Error("Invalid ICE server.");
  const urls = Array.isArray(value.urls)
    ? value.urls.filter((url): url is string => typeof url === "string")
    : typeof value.urls === "string" ? value.urls : [];
  if (urls.length === 0) throw new Error("ICE server URLs are missing.");
  return {
    urls,
    ...(typeof value.username === "string" ? { username: value.username } : {}),
    ...(typeof value.credential === "string" ? { credential: value.credential } : {}),
  };
};

const serverUrls = (server: RTCIceServer): string[] => typeof server.urls === "string" ? [server.urls] : server.urls;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const splitUrls = (value: string): string[] => value
  .split(",")
  .map((url) => url.trim())
  .filter(Boolean);
